/* eslint-disable @next/next/no-img-element */
import {
  ChangeEventHandler,
  ClipboardEventHandler,
  FC,
  FormEventHandler,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTheme } from 'styled-components';

import customAxios from '~/common/services/customAxios';
import { useToast } from '~/common/providers/toast';
import { IAugustoPost } from '~/common/interfaces/IAugustoPost';
import { TextArea } from '~/common/components/Form/TextArea';
import { Button } from '~/common/components/Button';

import { AugustoPostFormContainer, FilePickerContainer } from './styles';

type TFormData = Pick<IAugustoPost, 'text' | 'imageUrl'>;

interface IAugustoPostFormProps {
  onReturn: (afterCreate?: boolean) => void;
  updatePost: (postId: number, post: IAugustoPost) => void;
  updating: IAugustoPost;
}
export const AugustoPostForm: FC<IAugustoPostFormProps> = ({
  onReturn,
  updatePost,
  updating,
}) => {
  const { showToast } = useToast();
  const theme = useTheme();
  const fileRef = useRef<HTMLInputElement>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [formData, setFormData] = useState<TFormData>(
    updating
      ? {
          text: updating.text || '',
          imageUrl: updating.imageUrl || '',
        }
      : ({} as TFormData)
  );

  const handleDragEnter = (): void => {
    setIsDragging(true);
  };

  const handleDragLeave = (): void => {
    setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragging(false);
    const [droppedFile] = Array.from(event.dataTransfer.files);
    setImageFile(droppedFile);
  };

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = (ev) => {
    const file = ev.target.files[0];
    setImageFile(file);
  };

  const handlePaste = async (
    event: React.ClipboardEvent<HTMLTextAreaElement>
  ): Promise<void> => {
    const items = event.clipboardData?.items;
    const text = event.clipboardData?.getData('text/plain');

    // 1. Verifica se há imagem no clipboard
    const fileItem = Array.from(items).find((item) =>
      item.type.startsWith('image/')
    );
    if (fileItem) {
      const file = fileItem.getAsFile();
      if (file) {
        setImageFile(file);
        return;
      }
    }

    // 2. Se não for imagem, mas for link de imagem (termina com extensão de imagem)
    const isImageUrl = /^https?:\/\/.+\.(jpg|jpeg|png|webp|gif)$/i.test(text);
    if (isImageUrl) {
      try {
        const response = await fetch(text);
        const blob = await response.blob();
        const file = new File(
          [blob],
          `imagem_colada.${blob.type.split('/')[1]}`,
          { type: blob.type }
        );
        setImageFile(file);
      } catch (err) {
        showToast({
          type: 'error',
          description: 'Erro ao carregar imagem colada por link',
        });
      }
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result); // já inclui o prefixo data:image/...
      };
      reader.onerror = reject;
      reader.readAsDataURL(file); // garante formato completo Base64 + header
    });
  };

  const handleSubmit: FormEventHandler = async (ev) => {
    ev.preventDefault();
    setCreating(true);
    try {
      let imageBase64: string = null;
      if (imageFile) {
        imageBase64 = await fileToBase64(imageFile);
      }

      const payload = {
        text: formData.text,
        imageBase64: imageBase64 || updating?.imageUrl,
      };

      if (updating) {
        payload['id'] = updating.id;
      }

      const { data: upsert } = await customAxios.put<IAugustoPost>(
        '/Backes/posts',
        payload
      );

      if (updating) {
        updatePost(updating.id, upsert);
      }

      onReturn(true);
    } catch {
      showToast({
        type: 'error',
        description: 'Houve um erro ao criar o post',
      });
    } finally {
      setCreating(false);
    }
  };

  const renderingImage = useMemo(() => {
    if (imageFile) {
      return URL.createObjectURL(imageFile);
    }

    if (formData.imageUrl) {
      return formData.imageUrl;
    }
  }, [imageFile, formData]);

  return (
    <AugustoPostFormContainer onSubmit={handleSubmit}>
      <input
        hidden
        type="file"
        ref={fileRef}
        onChange={handleFileChange}
        accept=".jpeg, .jpg, .bmp, .png"
      />

      <TextArea
        name="text"
        required
        label={{
          label: 'Conteúdo',
          isRequired: true,
        }}
        value={formData.text}
        width="100%"
        onChange={(ev) =>
          setFormData((prev) => ({
            ...prev,
            text: ev.target.value,
          }))
        }
        onPaste={
          handlePaste as unknown as ClipboardEventHandler<HTMLInputElement>
        }
      />

      <FilePickerContainer
        onClick={() => fileRef.current.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Button
          height="48"
          width="100%"
          textOrIconColor={theme.neutral[200]}
          borderColor={theme.neutral[600]}
          backgroundColor={theme.neutral[800]}
          text={
            isDragging
              ? 'Solte o arquivo aqui'
              : 'Clique ou arraste a imagem aqui'
          }
        />
      </FilePickerContainer>

      {renderingImage && (
        <div style={{ marginTop: '12px', textAlign: 'center' }}>
          <img
            src={renderingImage}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              borderRadius: '8px',
            }}
          />
        </div>
      )}

      <div id="footer">
        <Button
          text="Voltar"
          type="button"
          width="100%"
          justifyContent="center"
          backgroundColor="transparent"
          textOrIconColor={theme.green[500]}
          onClick={() => onReturn(false)}
        />

        <Button
          text="Salvar"
          type="submit"
          width="100%"
          justifyContent="center"
          loader="bar"
          loaderColor="green.500"
          loading={creating}
        />
      </div>
    </AugustoPostFormContainer>
  );
};
